/**
 * Step 2 — Tenancy selection + Period picker.
 *
 * Section 1: fetch all tenancies for the selected tenant
 *   (include_participant=true so co-tenant tenancies surface).
 *   - 0 results → error message.
 *   - 1 result  → auto-select.
 *   - N results → radio list, sorted active-first then move_in_date desc.
 *
 * Section 2: year + month <Select> using MONTH_NAMES_DE labels.
 *   Visible only after a tenancy is selected.
 */
import React, { useEffect } from "react";
import { fetchAdminTenancies } from "../../../api/adminData";
import { MONTH_NAMES_DE } from "./MONTH_NAMES";
import { currentBusinessPeriod } from "../../../utils/datetime";

const labelCls =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]";
const selectCls =
  "h-[40px] rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none";

function tenancyLineLabel(t) {
  const unitLabel = [t?.unit_short_unit_id, t?.unit_title]
    .filter(Boolean)
    .join(" — ");
  const room = t?.room_name ? ` · ${t.room_name}` : "";
  const status = t?.status ? ` · ${t.status}` : "";
  const moveIn = t?.move_in_date ? ` · seit ${t.move_in_date}` : "";
  return `${unitLabel || "—"}${room}${status}${moveIn}`;
}

/** Active first, then move_in_date desc (newest first). Missing
 *  move_in_date sorts as 0 so it ends up last among same-status rows. */
function sortTenancies(list) {
  return [...list].sort((a, b) => {
    const aActive = a?.status === "active" ? 0 : 1;
    const bActive = b?.status === "active" ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    const aDate = a?.move_in_date
      ? new Date(a.move_in_date).getTime()
      : 0;
    const bDate = b?.move_in_date
      ? new Date(b.move_in_date).getTime()
      : 0;
    return bDate - aDate;
  });
}

export default function Step2TenancyPeriod({ state, updateState }) {
  // Load tenancies on first entry (and on tenant change → tenancies reset to null).
  useEffect(() => {
    if (!state.selectedTenant) return;
    if (state.tenancies !== null) return; // already loaded
    let cancelled = false;
    updateState({ tenanciesLoading: true });
    fetchAdminTenancies({
      tenant_id: state.selectedTenant.id,
      include_participant: true,
      limit: 50,
    })
      .then((items) => {
        if (cancelled) return;
        const list = Array.isArray(items) ? items : [];
        const sorted = sortTenancies(list);
        const patch = { tenancies: sorted, tenanciesLoading: false };
        if (sorted.length === 1 && !state.selectedTenancy) {
          patch.selectedTenancy = sorted[0];
        }
        updateState(patch);
      })
      .catch(() => {
        if (cancelled) return;
        updateState({ tenancies: [], tenanciesLoading: false });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [state.selectedTenant, state.tenancies]);

  const currentYear = currentBusinessPeriod().year;
  const yearOptions = [-2, -1, 0, 1, 2].map((d) => currentYear + d);

  return (
    <div className="space-y-6">
      <section>
        <div className={labelCls}>Mietverhältnis</div>
        {state.tenanciesLoading ? (
          <div className="text-sm text-[#64748b] dark:text-[#6b7a9a]">
            Lade Mietverhältnisse…
          </div>
        ) : Array.isArray(state.tenancies) && state.tenancies.length === 0 ? (
          <div className="rounded-[8px] border border-red-300/40 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/[0.08] dark:text-red-300">
            Kein Mietverhältnis für diesen Mieter. Bitte zurück und einen
            anderen Mieter wählen.
          </div>
        ) : (
          <ul className="space-y-2">
            {(state.tenancies || []).map((t) => {
              const checked = state.selectedTenancy?.id === t.id;
              return (
                <li key={t.id}>
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-[8px] border p-3 transition-colors ${
                      checked
                        ? "border-[#378ADD] bg-[#378ADD]/[0.08] dark:bg-[#378ADD]/[0.12]"
                        : "border-black/10 bg-white hover:bg-slate-50 dark:border-white/[0.07] dark:bg-[#0f1320] dark:hover:bg-[#141824]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="tenancy"
                      checked={checked}
                      onChange={() => updateState({ selectedTenancy: t })}
                      className="mt-1"
                    />
                    <span className="text-sm text-[#0f172a] dark:text-[#eef2ff]">
                      {tenancyLineLabel(t)}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {state.selectedTenancy ? (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Jahr</label>
            <select
              value={state.billingYear}
              onChange={(e) =>
                updateState({ billingYear: Number(e.target.value) })
              }
              className={`${selectCls} w-full`}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Monat</label>
            <select
              value={state.billingMonth}
              onChange={(e) =>
                updateState({ billingMonth: Number(e.target.value) })
              }
              className={`${selectCls} w-full`}
            >
              {MONTH_NAMES_DE.map((name, idx) => (
                <option key={name} value={idx + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          {/* T2.6.1: optional per-invoice payment term. Empty input
              keeps the backend default (currently hardcoded 10 days).
              Value is sent to backend as payment_term_days. */}
          <div className="sm:col-span-2">
            <label className={labelCls}>Zahlungsfrist (Tage)</label>
            <input
              type="number"
              min={0}
              max={365}
              placeholder="z.B. 14 (leer = 30 Tage Standard)"
              value={
                state.paymentTermDays === null ||
                state.paymentTermDays === undefined
                  ? ""
                  : state.paymentTermDays
              }
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  updateState({ paymentTermDays: null });
                  return;
                }
                const n = Number(raw);
                if (Number.isFinite(n) && n >= 0 && n <= 365) {
                  updateState({ paymentTermDays: n });
                }
              }}
              className={`${selectCls} w-full`}
            />
            <p className="mt-1 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
              Berechnet ab Rechnungsdatum (nicht ab heute). Leer
              lassen für Standard (30 Tage, ab Organisations-
              einstellungen).
            </p>
          </div>
        </section>
      ) : null}
    </div>
  );
}
